<template>
  <footer id="footer" class="footer" :lang="currentLanguage === 'ZH' ? 'zh-CN' : 'en'">
    <div class="footer__inner">
      <div class="footer__brand-row">
        <router-link class="footer__logo" to="/" :aria-label="t('footer.homeAria')">
          <img src="/static/images/footer/anovator-navigation-logo.svg" alt="ANOVATOR" width="106.108" height="16">
        </router-link>
      </div>

      <div class="footer__navigation">
        <section v-for="group in navigation" :key="group.id" class="footer__group" :data-footer-group="group.id" :aria-label="group.label">
          <h2><router-link v-if="group.to" :to="group.to">{{ group.label }}</router-link><template v-else>{{ group.label }}</template></h2>
          <ul>
            <li v-for="item in group.items" :key="item.label">
              <button v-if="item.action === 'contact'" type="button" aria-haspopup="dialog" @click="requestContactModal($event.currentTarget)">{{ item.label }}</button>
              <router-link v-else-if="item.to" :to="item.to">{{ item.label }}</router-link>
              <a v-else-if="item.href" :href="item.href" target="_blank" rel="noopener noreferrer">{{ item.label }}</a>
              <span v-else>{{ item.label }}</span>
            </li>
          </ul>
        </section>

        <section class="footer__contact-group" :aria-label="t('footer.contactAria')">
          <h2>{{ currentLanguage === 'ZH' ? '联系我们' : 'Contact Us' }}</h2>
        <div class="footer__contacts" :aria-label="t('footer.contactAria')">
          <component
            v-for="contact in contacts"
            :is="contact.qrImage ? 'button' : contact.href ? 'a' : 'span'"
            :key="contact.type"
            class="footer__contact"
            :href="contact.href"
            :type="contact.qrImage ? 'button' : undefined"
            :target="contact.type === 'whatsapp' ? '_blank' : undefined"
            :rel="contact.type === 'whatsapp' ? 'noopener noreferrer' : undefined"
            @click="handleContactClick(contact, $event)"
          >
            <span class="footer__contact-label">{{ contact.label }}</span>
          </component>
        </div>
          <button v-if="wechatContact && wechatContact.qrImage" class="footer__inline-qr" type="button" :aria-label="wechatContact.dialogTitle" @click="handleContactClick(wechatContact, $event)">
            <img :src="wechatContact.qrImage" :alt="wechatContact.dialogTitle" width="120" height="120">
          </button>
          <div v-if="whatsappContact && whatsappQr" class="footer__whatsapp-qr">
            <img :src="whatsappQr" alt="WhatsApp QR" width="107" height="107">
            <a :href="whatsappContact.href" target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a>
          </div>
        </section>
      </div>

        <ul class="footer__socials" :aria-label="t('footer.socialAria')">
          <li v-for="social in socials" :key="social.id" :class="`footer__social--${social.id}`">
            <a v-if="social.href" class="footer__social-icon" :href="social.href" target="_blank" rel="noopener noreferrer" :aria-label="social.label">
              <img :src="social.icon" alt="">
            </a>
            <button v-else-if="social.qrImage" class="footer__social-icon" type="button" :aria-label="social.label" @click="openSocialDialog(social, $event)">
              <img :src="social.icon" alt="">
            </button>
            <span v-else class="footer__social-icon" role="img" :aria-label="social.label"><img :src="social.icon" alt=""></span>
          </li>
        </ul>

      <div class="footer__legal-row" role="group" :aria-label="t('footer.legalAria')">
        <p>COPYRIGHT © 2026 ZHAIKER ALL RIGHTS RESERVED</p>
        <nav class="footer__policies" :aria-label="t('footer.legalAria')">
          <router-link to="/privacy-policy">{{ currentLanguage === 'ZH' ? '隐私政策' : 'Privacy Policy' }}</router-link>
          <button type="button" data-anovator-cookie-settings>{{ currentLanguage === 'ZH' ? 'Cookie 设置' : 'Cookie Settings' }}</button>
        </nav>
        <p v-if="showIcp" class="footer__public-security-record">
          <img src="/static/images/footer/public-security-record.png" alt="" width="16" height="18" aria-hidden="true">
          <span>鄂公网安备42011502002042号</span>
        </p>
      </div>
    </div>

    <teleport to="body">
      <div v-if="activeQrSocial" class="footer-social-dialog-overlay" @click.self="closeSocialDialog">
        <section
          class="footer-social-dialog"
          :class="{ 'is-contact-wechat': activeQrSocial.id === 'contact-wechat' }"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="activeQrSocial.id === 'contact-wechat' ? undefined : `${activeQrSocial.id}-dialog-title`"
          :aria-label="activeQrSocial.id === 'contact-wechat' ? activeQrSocial.dialogTitle : undefined"
        >
          <button class="footer-social-dialog__close" type="button" :aria-label="t('contact.closeAria')" @click="closeSocialDialog">
            <img src="/static/images/footer/social-dialog-close.svg" alt="" width="24" height="24" aria-hidden="true">
          </button>
          <div class="footer-social-dialog__qr" :class="{ 'is-contact-wechat': activeQrSocial.id === 'contact-wechat' }">
            <img :class="{ 'is-channels': activeQrSocial.id === 'channels', 'is-contact-wechat': activeQrSocial.id === 'contact-wechat' }" :src="activeQrSocial.qrImage" :alt="activeQrSocial.dialogTitle">
          </div>
          <p v-if="activeQrSocial.id !== 'contact-wechat'" :id="`${activeQrSocial.id}-dialog-title`">{{ activeQrSocial.dialogTitle }}</p>
        </section>
      </div>
    </teleport>
  </footer>
</template>

<script>
import { contactWayAPI, solutionAPI } from '@/utils/api'
import QRCode from 'qrcode'

const { getFooterSocials, shouldShowIcp } = require('@/data/footer-content.cjs')
const { createWhatsAppUrl, requestContactModal } = require('@/utils/contact-modal.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')
const { getFooterNavigation } = require('@/data/footer-navigation.cjs')
const { createIndustryMenu } = require('@/data/site-navigation.cjs')
const { normalizeIndustryCategoriesResponse, normalizeIndustryTopicsResponse } = require('@/utils/industries-data.cjs')

export default {
  name: 'FooterSection',
  mixins: [siteLanguageMixin],
  data() {
    return {
      contactRequestGuard: createLatestSiteRequestGuard(),
      contacts: [],
      industryRequestGuard: createLatestSiteRequestGuard(),
      industryCategories: [],
      whatsappQr: '',
      activeQrSocial: null,
      socialDialogTrigger: null
    }
  },
  computed: {
    navigation() { return getFooterNavigation(this.currentLanguage, this.industryCategories) },
    wechatContact() { return this.contacts.find(contact => contact.type === 'wechat') },
    whatsappContact() { return this.contacts.find(contact => contact.type === 'whatsapp') },
    socials() {
      return getFooterSocials(this.currentLanguage)
    },
    showIcp() {
      return shouldShowIcp(this.currentLanguage)
    }
  },
  created() {
    this.loadContactWay()
    this.loadNavigation()
  },
  watch: {
    currentLanguage() {
      this.closeSocialDialog(false)
      this.loadContactWay()
      this.loadNavigation()
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleSocialDialogKeydown)
  },
  methods: {
    requestContactModal,
    async loadNavigation() {
      const request = this.industryRequestGuard.start(this.currentLanguage)
      this.industryCategories = []
      try {
        const categories = normalizeIndustryCategoriesResponse(await solutionAPI.getCategories(request.siteType))
        const loaded = await Promise.all(categories.map(async category => {
          const topics = normalizeIndustryTopicsResponse(await solutionAPI.getTopics(category.categoryId, request.siteType))
          return createIndustryMenu(category, topics)
        }))
        if (this.industryRequestGuard.isCurrent(request, this.currentLanguage)) this.industryCategories = loaded
      } catch (_) {
        // Unavailable destinations remain plain text, never invented routes.
      }
    },
    openSocialDialog(social, event) {
      this.socialDialogTrigger = event.currentTarget
      this.activeQrSocial = social
      document.addEventListener('keydown', this.handleSocialDialogKeydown)
    },
    closeSocialDialog(restoreFocus = true) {
      if (!this.activeQrSocial) return
      this.activeQrSocial = null
      document.removeEventListener('keydown', this.handleSocialDialogKeydown)
      if (restoreFocus) this.$nextTick(() => this.socialDialogTrigger?.focus())
      this.socialDialogTrigger = null
    },
    handleSocialDialogKeydown(event) {
      if (event.key === 'Escape') this.closeSocialDialog()
    },
    handleContactClick(contact, event) {
      if (!contact.qrImage) return
      event.preventDefault()
      this.openSocialDialog(contact, event)
    },
    async loadContactWay() {
      const request = this.contactRequestGuard.start(this.currentLanguage)
      this.contacts = []
      this.whatsappQr = ''
      try {
        const contact = await contactWayAPI.getCurrent(request.siteType)
        if (!this.contactRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.contacts = request.siteType === 'ZH'
          ? [
              { type: 'phone', label: text(contact?.phone), icon: '/static/images/contact-modal/phone.svg' },
              { type: 'email', label: text(contact?.email), href: `mailto:${text(contact?.email)}`, icon: '/static/images/footer/email.svg' },
              { type: 'wechat', label: text(contact?.wechatNo) || (text(contact?.wechatQrCodeUrl) ? '微信' : ''), icon: '/static/images/footer/wechat.svg', qrImage: text(contact?.wechatQrCodeUrl), id: 'contact-wechat', dialogTitle: '微信二维码' }
            ].filter(item => item.label && (item.type === 'phone' || item.type === 'wechat' || item.href || item.qrImage))
          : [
              { type: 'email', label: text(contact?.email), href: `mailto:${text(contact?.email)}`, icon: '/static/images/footer/email.svg' },
              { type: 'whatsapp', label: text(contact?.whatsapp), href: createWhatsAppUrl(contact?.whatsapp), icon: '/static/images/contact-modal/whatsapp.svg' }
            ].filter(item => item.label && item.href)
        const whatsapp = this.whatsappContact
        if (whatsapp) {
          const qr = await QRCode.toDataURL(whatsapp.href, { width: 214, margin: 4 }).catch(() => '')
          if (this.contactRequestGuard.isCurrent(request, this.currentLanguage)) this.whatsappQr = qr
        }
      } catch (_) {
        if (!this.contactRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.contacts = []
      }
    }
  }
}

function text(value) {
  return typeof value === 'string' ? value.trim() : ''
}
</script>

<style scoped src="../assets/styles/footer-navigation.css"></style>

<style scoped>
.footer-social-dialog-overlay,
.footer-social-dialog-overlay * {
  box-sizing: border-box;
}

.footer-social-dialog-overlay {
  position: fixed;
  top: var(--global-header-height, 44px);
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1001;
  display: flex;
  width: 100vw;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(215, 215, 215, 0.5);
  backdrop-filter: blur(10px);
}

.footer-social-dialog {
  position: relative;
  width: 636px;
  height: 343px;
  overflow: hidden;
  background: #fff;
  border-radius: 28px;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
}

.footer-social-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}

.footer-social-dialog__close img {
  display: block;
  width: 24px;
  height: 24px;
}

.footer-social-dialog__close:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

.footer-social-dialog__qr {
  position: absolute;
  top: 60px;
  left: 50%;
  width: 190px;
  height: 190px;
  overflow: hidden;
  background: #878787;
  transform: translateX(-50%);
}

.footer-social-dialog__qr img {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.footer-social-dialog__qr img.is-channels {
  position: absolute;
  top: -87px;
  left: 50%;
  display: block;
  width: 254px;
  height: 331px;
  max-width: none;
  object-fit: cover;
  transform: translateX(-50%);
}

.footer-social-dialog.is-contact-wechat {
  display: flex;
  width: max-content;
  height: auto;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - var(--global-header-height, 44px) - 48px);
  align-items: center;
  justify-content: center;
  padding: 40px 24px 24px;
}

.footer-social-dialog.is-contact-wechat .footer-social-dialog__close {
  top: 10px;
  right: 10px;
  display: block;
}

.footer-social-dialog__qr.is-contact-wechat {
  position: static;
  display: flex;
  width: auto;
  height: auto;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background: transparent;
  transform: none;
}

.footer-social-dialog__qr img.is-contact-wechat {
  width: auto;
  height: auto;
  max-width: min(540px, calc(100vw - 96px));
  max-height: calc(100vh - var(--global-header-height, 44px) - 112px);
  object-fit: contain;
}

.footer-social-dialog p {
  position: absolute;
  right: 0;
  bottom: 81px;
  left: 0;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
  font-family: 'MiSans-Medium', 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 16px;
  line-height: normal;
  text-align: center;
  transform: translateY(100%);
}

@media (max-width: 700px) {

  .footer-social-dialog-overlay {
    padding: 16px;
  }

  .footer-social-dialog {
    width: min(400px, calc(100vw - 32px));
    height: 335px;
    border-radius: 14px;
  }

  .footer-social-dialog__close {
    display: none;
  }

  .footer-social-dialog__qr {
    top: 56px;
  }

  .footer-social-dialog p {
    bottom: 77px;
  }
}
</style>
